<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Inventory Checks</h1>
				<p class="text-muted mb-0">
					A periodic count of stock: each counted line corrects the ledger to
					what is on the shelf.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- A check is freely scoped: the person opens one, then records the
		     locations or products they counted on its detail page. -->
		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-0">Start a Check</h2>
				<p class="text-muted mb-3 small">
					Open a check, then record its counted lines. Frequency is the
					district's practice; more is better.
				</p>

				<form class="row g-2 align-items-end" @submit.prevent="createCheck">
					<div class="col-12 col-md-8 col-lg-6">
						<label class="form-label" for="checkNote">Note</label>
						<input
							id="checkNote"
							v-model="newNote"
							type="text"
							class="form-control"
							placeholder="e.g. Monthly storeroom count"
						/>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							type="submit"
							text="Start check"
							icon="bi bi-clipboard-check"
							variant="primary"
							:loading="creating"
						/>
					</div>
				</form>
			</div>
		</div>

		<div class="card mt-4">
			<div class="card-body">
				<h2 class="h5 mb-3">Checks</h2>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="checks.length === 0"
					class="text-center py-4 text-muted"
				>
					No checks recorded yet.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Check</th>
								<th scope="col">Started</th>
								<th scope="col">Lines</th>
								<th scope="col">Note</th>
								<th scope="col">Status</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="check in checks" :key="check.id">
								<td class="fw-semibold">#{{ check.id }}</td>
								<td>
									<TimeRelativeWithTooltip :time="check.created" />
								</td>
								<td>{{ check.line_count }}</td>
								<td class="text-muted">{{ check.note || "—" }}</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="
											check.completed ? 'bg-success' : 'bg-warning text-dark'
										"
									>
										{{ check.completed ? "Complete" : "Open" }}
									</span>
								</td>
								<td class="text-end">
									<RouterLink
										class="btn btn-sm btn-outline-secondary"
										:to="`/_/inventory/checks/${check.id}`"
									>
										View
									</RouterLink>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { CheckCreate, InventoryCheck } from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const checks = ref<InventoryCheck[]>([]);
const loading = ref(false);
const error = ref("");
const newNote = ref("");
const creating = ref(false);

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// The whole check collection through the generic QUERY endpoint (the
		// legacy GET list's replacement, doc/in-progress/query.md §11 Phase 4):
		// the page rows are the same shape, and the walk holds every one of them
		// as the un-paged fetch it replaces did.
		const page = await apiClient.JSONQueryAll<InventoryCheck>(
			"/api/inventory/check",
		);
		checks.value = page.items;
	} catch (e: any) {
		error.value = e?.message || "Failed to load inventory checks.";
		showNotification("Failed to load checks", error.value);
	} finally {
		loading.value = false;
	}
}

async function createCheck() {
	creating.value = true;
	const note = newNote.value.trim();
	const body: CheckCreate = note !== "" ? { note } : {};
	try {
		const created = await apiClient.JSONPost<InventoryCheck>(
			"/api/inventory/check",
			body,
		);
		// Re-read the list from the server so the order and line counts are the
		// server's, not a guess.
		await load();
		newNote.value = "";
		showNotification(
			"Check started",
			`Check #${created.id} is open. Record its counted lines on its page.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to start check",
			e?.message || "Please try again.",
		);
	} finally {
		creating.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
